/**
 * @file: useTextInputAutoResize.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCallback } from 'react';
import {
  LayoutChangeEvent,
  NativeSyntheticEvent,
  TextInputContentSizeChangeEventData,
} from 'react-native';
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export const useTextInputAutoResize = ({
  minHeight,
  maxHeight,
  padding,
}: {
  minHeight: number;
  maxHeight: number;
  padding: number;
}) => {
  const height = useSharedValue(minHeight);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const h = e.nativeEvent.layout.height;
      if (height.value !== h) {
        height.value = h;
      }
    },
    [height],
  );

  const onContentSizeChange = useCallback(
    (e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      const h = Math.round(e.nativeEvent.contentSize.height);
      height.value = h + padding * 2;
    },
    [height, padding],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    height: withTiming(Math.min(maxHeight, Math.max(height.value, minHeight)), {
      duration: 50,
    }),
  }));

  return { animatedStyle, onLayout, onContentSizeChange };
};
